import { useEffect, useRef, useState } from "react";
import { Download, Eye, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, formatApiError } from "../../lib/api";
import { envoyerParMorceaux, SEUIL_MORCEAUX } from "../../lib/uploadMorceaux";

// Carte « Fichiers du logo officiel » — demande du propriétaire (2026-10-20) :
// AJOUTER ou EFFACER un fichier (image, PDF, AI…) dans le bloc. Les fichiers
// historiques du site sont pré-chargés par le backend ; l'admin peut effacer
// n'importe quelle entrée et ajouter les siennes (libellé libre + fichier).
// Lecture : tout le personnel ; ajout/effacement : administrateurs seulement
// (le backend l'impose avec require_admin).

const TAILLE = (o) => (o >= 1048576 ? `${(o / 1048576).toFixed(1)} Mo` : o > 0 ? `${Math.max(1, Math.round(o / 1024))} Ko` : "");
const DATE = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-CA", { year: "numeric", month: "short", day: "numeric" }) : "");

export default function LogoFichiersCard({ isAdmin = false }) {
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  const [fichier, setFichier] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progres, setProgres] = useState(null); // % d'envoi d'un gros fichier (par morceaux)
  const fileRef = useRef(null);

  const charger = async () => {
    try {
      const r = await api.get("/admin/logo-fichiers");
      setItems(r.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  useEffect(() => { charger(); }, []);

  const ajouter = async () => {
    if (!label.trim()) { toast.error("Écrivez un libellé pour le fichier"); return; }
    if (!fichier) { toast.error("Choisissez le fichier à ajouter"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("label", label.trim());
      // Petit fichier → envoi direct ; gros (> 8 Mo) → par morceaux (illimité)
      if (fichier.size > SEUIL_MORCEAUX) {
        setProgres(0);
        fd.append("upload_id", await envoyerParMorceaux(fichier, setProgres));
      } else {
        fd.append("file", fichier);
      }
      await api.post("/admin/logo-fichiers", fd);
      toast.success(`Fichier « ${label.trim()} » ajouté`);
      setLabel(""); setFichier(null);
      if (fileRef.current) fileRef.current.value = "";
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); setProgres(null); }
  };

  const effacer = async (f) => {
    if (!window.confirm(`Effacer « ${f.label} » de la liste ?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/logo-fichiers/${f.id}`);
      toast.success(`« ${f.label} » effacé`);
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const urlVoir = (f) => (f.static ? f.href : `${API_BASE}/api/admin/logo-fichiers/${f.id}/fichier`);
  const inputCls = "w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-ivory focus:border-gold focus:outline-none transition-colors";

  return (
    <div className="bg-surface border border-ivory/10 rounded-xl p-7 mt-8" data-testid="logo-downloads-card">
      <div className="flex items-center gap-6 flex-wrap">
        <img src="/logo-gold-t.png" alt="Logo officiel Mr Trophée" className="h-20 w-auto" data-testid="logo-downloads-preview" />
        <div className="flex-1 min-w-[220px]">
          <p className="font-semibold">Fichiers du logo officiel</p>
          <p className="text-xs text-ivory/70 mt-1">
            PNG pour le web et les courriels (fond transparent) — versions française « Depuis 25 ans »
            et anglaise « Since 25 years ». AI et PDF vectoriels pour l'impression
            et l'agrandissement sans perte (version tracée, style illustré). La version « dessin seul »
            contient uniquement le trophée, les mains et les lettres — sans la plaque noire — pour poser
            le logo sur n'importe quel fond. Carte de visite, papier à en-tête et enveloppe prêts à
            imprimer (300 dpi, fond perdu inclus). La signature courriel se colle dans les réglages
            de vos boîtes HostPapa (mode HTML).
          </p>
        </div>
      </div>

      {/* Ajout d'un fichier — administrateurs seulement */}
      {isAdmin && (
        <div className="flex flex-col sm:flex-row gap-2 mt-5 pt-5 border-t border-ivory/10">
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={200}
            placeholder="Libellé du fichier (ex. : PNG fond noir)"
            data-testid="logo-fichier-label" className={`${inputCls} sm:flex-1`} />
          <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.gif,.svg,.pdf,.ai,.eps,.html,.zip"
            onChange={(e) => setFichier(e.target.files?.[0] || null)}
            data-testid="logo-fichier-fichier"
            className="text-xs text-ivory/70 file:mr-3 file:rounded-full file:border-0 file:bg-ivory/10 file:px-3 file:py-1.5 file:text-xs file:text-ivory hover:file:bg-gold hover:file:text-ink file:transition-colors sm:max-w-[240px]" />
          <button type="button" onClick={ajouter} disabled={busy} data-testid="logo-fichier-ajouter"
            className="inline-flex items-center justify-center gap-1.5 bg-gold text-ink text-xs font-semibold px-4 py-2 rounded-full hover:bg-champagne transition-colors disabled:opacity-60 shrink-0">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            {progres !== null ? `Envoi… ${progres} %` : "Ajouter"}
          </button>
        </div>
      )}
      {progres !== null && (
        <div className="h-1.5 bg-ivory/10 rounded-full overflow-hidden mt-3" data-testid="logo-fichier-progression">
          <div className="h-full bg-gold transition-all duration-300" style={{ width: `${progres}%` }} />
        </div>
      )}

      {/* Fichiers enregistrés */}
      <div className="space-y-2 mt-5">
        {items.map((f) => (
          <div key={f.id} className="flex items-center justify-between gap-3 flex-wrap border border-ivory/10 rounded-lg px-4 py-3"
            data-testid={`logo-fichier-item-${f.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate" data-testid={`logo-fichier-item-label-${f.id}`}>{f.label}</p>
              <p className="text-xs text-ivory/55 truncate">
                {f.static ? "Fichier du site" : `${f.filename} · ${TAILLE(f.size)} · ${DATE(f.created_at)}`}
              </p>
            </div>
            {/* Pilules cadrées sur mobile (grille 2 colonnes) ; flex sur grand écran */}
            <div className="grid grid-cols-2 gap-2 w-full sm:w-auto sm:flex sm:flex-wrap sm:justify-end shrink-0">
              <a href={urlVoir(f)} target="_blank" rel="noopener noreferrer" data-testid={`logo-fichier-voir-${f.id}`}
                className="inline-flex items-center justify-center gap-1.5 border border-emerald-500/50 text-emerald-300 text-xs px-3.5 py-1.5 rounded-full hover:bg-emerald-500 hover:text-ink transition-colors">
                <Eye className="w-3.5 h-3.5" /> Voir
              </a>
              <a href={f.static ? f.href : `${urlVoir(f)}?dl=1`} download={f.static ? true : undefined}
                data-testid={`logo-fichier-telecharger-${f.id}`}
                className="inline-flex items-center justify-center gap-1.5 border border-ivory/25 text-ivory/70 text-xs px-3.5 py-1.5 rounded-full hover:border-gold hover:text-gold transition-colors">
                <Download className="w-3.5 h-3.5" /> Télécharger
              </a>
              {isAdmin && (
                <button type="button" onClick={() => effacer(f)} disabled={busy} data-testid={`logo-fichier-effacer-${f.id}`}
                  className="col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-1.5 border border-red-500/40 text-red-300 text-xs px-3.5 py-1.5 rounded-full hover:bg-red-500 hover:text-white transition-colors disabled:opacity-60">
                  <Trash2 className="w-3.5 h-3.5" /> Effacer
                </button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-xs text-ivory/45" data-testid="logo-fichier-vide">Aucun fichier — ajoutez le premier ci-dessus.</p>
        )}
      </div>
    </div>
  );
}
